import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Hexagon, ArrowRight, LayoutDashboard, Quote } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  MouseParallaxStage,
  BentoHeadline,
  MetricTile,
  Asterisk,
  BentoTag,
  BentoTile,
} from "@/components/pitch/Bento";

const HERO_VIDEO_URL = "/pitch/hero.mp4";

/**
 * Public marketing landing — the front door for OmniDraftLab.
 *
 * Visually identical to slide 1 of the pitch deck (video bg, cream "20" tile,
 * cyan "01" tile, asterisk, editorial typography) so the brand reads the
 * same whether a visitor lands here or jumps mid-deck. Differences vs the
 * deck slide:
 *   - Top-right chrome shows a contextual auth link (Sign in / Open dashboard)
 *     instead of the deck's PDF / fullscreen / close controls.
 *   - The bottom eyebrow row ("NFL-Caliber Evaluation · …") now anchors two
 *     CTA buttons: primary "Continue the pitch →" and secondary "Sign in /
 *     Open dashboard" depending on auth state.
 *   - No bottom-of-page slide-dot navigation.
 */
export default function LandingPage() {
  useDocumentTitle("OmniDraftLab · Front-office-grade NFL draft preparation");
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const isAuthed = !!token;

  return (
    <div className="min-h-screen w-full bg-[#04111A] text-1 relative overflow-hidden">
      {/* Top chrome */}
      <header className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 grid place-items-center shadow-[0_8px_24px_-8px_rgba(34,211,238,0.6)]">
            <Hexagon className="h-4 w-4 text-[#04111A]" strokeWidth={2.4} />
          </div>
          <span className="font-semibold tracking-tight text-1">OmniDraftLab</span>
        </Link>

        {isAuthed ? (
          <Link
            to="/app/dashboard"
            data-testid="landing-nav-dashboard"
            className="text-2 hover:text-1 text-sm flex items-center gap-1.5 transition"
          >
            <LayoutDashboard className="h-4 w-4" />
            Open dashboard
          </Link>
        ) : (
          <Link
            to="/login"
            data-testid="landing-nav-signin"
            className="text-2 hover:text-1 text-sm transition"
          >
            Sign in
          </Link>
        )}
      </header>

      {/* Hero stage — whole content block (headline + tiles + eyebrow +
          quote + CTAs) vertically centered as one cohesive unit. With the
          small pull-quote this keeps the focal point near viewport-center
          on standard heights and balances the white space evenly above
          and below on tall monitors instead of pooling in the middle. */}
      <main className="relative z-10 min-h-screen flex flex-col justify-center px-6 sm:px-10 lg:px-16 max-w-[1380px] mx-auto py-24">
        <MouseParallaxStage className="relative w-full">
          {/* Background video — same treatment as deck slide 1.
              Multi-source: WebM/VP9 first (smallest + works on every modern
              browser including codec-stripped headless ones), MP4/H.264 as
              the universal fallback. `poster` paints a sharp branded still
              frame instantly so visitors never see the dark void while the
              video buffers. */}
          <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
            <video
              className="absolute inset-0 w-full h-full object-cover opacity-55"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              poster="/pitch/hero-poster.jpg"
            >
              <source src="/pitch/hero.webm" type="video/webm" />
              <source src={HERO_VIDEO_URL} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-[#04111A]/60" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#04111A]/20 to-[#04111A]/85" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#04111A]/65 via-transparent to-transparent" />
          </div>

          {/* Hero grid */}
          <div className="grid grid-cols-12 gap-4 sm:gap-5 items-stretch">
            {/* Editorial headline tile */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 0.65, 0.2, 1] }}
              className="col-span-12 lg:col-span-8 relative"
            >
              <div className="flex items-center gap-3 mb-6">
                <BentoTag tone="cyan">OmniDraftLab</BentoTag>
                <span className="text-3 text-[10px] uppercase tracking-[0.22em]">Pitch · 2026</span>
              </div>
              <BentoHeadline size="xl" parallax={4} className="drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
                Pre-Draft<br />
                <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-blue-500 bg-clip-text text-transparent">Intelligence.</span><br />
                Strategy. <span className="italic font-light">Positioning.</span>
              </BentoHeadline>
              <p className="text-2 text-base sm:text-xl mt-7 max-w-2xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.5)]">
                We prepare athletes and families for how the NFL <em>actually</em> works.
              </p>

              {/* Primary CTAs — sit directly with the headline so they read as
                  the main action area; eyebrow chips + pull-quote support
                  underneath. */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 0.65, 0.2, 1] }}
                className="mt-7 flex flex-wrap gap-3"
              >
                <button
                  type="button"
                  data-testid="landing-cta-pitch"
                  onClick={() => navigate("/pitch")}
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-cyan-300 text-[#04111A] font-semibold hover:bg-cyan-200 transition shadow-[0_18px_50px_-15px_rgba(34,211,238,0.7)] text-base"
                >
                  Continue the pitch <ArrowRight className="h-4 w-4" />
                </button>

                {isAuthed ? (
                  <button
                    type="button"
                    data-testid="landing-cta-dashboard"
                    onClick={() => navigate("/app/dashboard")}
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl border border-white/15 bg-white/[0.03] text-1 hover:bg-white/[0.08] transition text-base"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Open dashboard
                    <span className="text-3 text-xs ml-1">· {user?.full_name?.split(" ")[0]}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="landing-cta-signin"
                    onClick={() => navigate("/login")}
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl border border-white/15 bg-white/[0.03] text-1 hover:bg-white/[0.08] transition text-base"
                  >
                    <Hexagon className="h-4 w-4" /> Sign in
                  </button>
                )}
              </motion.div>

              <div className="absolute -top-2 right-4 hidden lg:block">
                <Asterisk tone="rose" size={48} />
              </div>
            </motion.div>

            {/* Metric stack — right column */}
            <div className="col-span-12 lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-3 sm:gap-4">
              <MetricTile
                tone="cream"
                eyebrow="NFL personnel"
                value="20"
                label="Years inside the league"
                sub="Carolina · Tennessee"
                parallax={10}
                delay={0.15}
                testId="landing-metric-years"
              />
              <MetricTile
                tone="cyan"
                eyebrow="Engagement"
                value="01"
                label="Application-only"
                sub="Limited clients per draft class"
                parallax={14}
                delay={0.25}
                testId="landing-metric-app"
              />
            </div>
          </div>

          {/* Eyebrow row — kept as-is per brand voice */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45, ease: [0.22, 0.65, 0.2, 1] }}
            className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-3 text-[11px] uppercase tracking-[0.22em]"
          >
            <span>NFL-Caliber Evaluation</span>
            <span className="text-3">·</span>
            <span>Front-Office Strategy</span>
            <span className="text-3">·</span>
            <span>Family Advisory</span>
          </motion.div>

          {/* Pull-quote — placeholder copy (bracketed bits = swap me).
              Sits between eyebrow + CTAs as a quiet endorsement; sized down
              intentionally so it never competes with the editorial headline
              or the metric tiles for visual weight. */}
          <BentoTile
            tone="glass"
            parallax={6}
            delay={0.5}
            testId="landing-pullquote"
            className="mt-6 max-w-xl p-4 sm:p-5 relative"
          >
            <Quote className="h-3.5 w-3.5 text-cyan-300/80 absolute top-4 left-4" strokeWidth={1.6} />
            <blockquote className="text-1 text-sm sm:text-[15px] italic font-light leading-snug pl-7 pr-1">
              They told us things our agent never did — and they told us
              <span className="text-cyan-300 not-italic font-semibold"> early.</span>
              <span className="block mt-1 not-italic font-light text-2 text-[12px] leading-relaxed">
                By the time the call came, our son knew exactly who was on the line.
              </span>
            </blockquote>
            <figcaption className="mt-3 pl-7 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-1 text-[11px] font-semibold">— [Mother of a recent draft prospect]</span>
              <span className="text-3 text-[9px] uppercase tracking-[0.2em]">[Pre-Draft · 2024]</span>
            </figcaption>
          </BentoTile>
        </MouseParallaxStage>
      </main>
    </div>
  );
}
